import { useEffect, useState } from 'react'

import { PSA_CHART_TYPES } from './chartTypes'
import { ChartHeader } from '@/Atomic/Molecules/Charts/header/ChartHeader'
import { AcBarChart } from '@/Atomic/Organisms/Charts/acBar/AcBarChart'
import { ChartData } from '@/Atomic/Organisms/Charts/acBar/dataConverter'
import { AcLinesChart } from '@/Atomic/Organisms/Charts/acLines/AcLinesChart'
import { AcPieChart } from '@/Atomic/Organisms/Charts/acPie/AcPieChart'
import { AcRadialChart } from '@/Atomic/Organisms/Charts/acRadial/AcRadialChart'
import {
  ChartFilter as LegacyChartFilter,
  CurrentChartFilter,
} from '@/Atomic/Organisms/Charts/charts'
import { GaugeChart } from '@/Atomic/Organisms/Charts/Gauge/GaugeChart'
import { Watchlist } from '@/Atomic/Organisms/Charts/Watchlist/Watchlist'
import { LoadingOverlay } from '@/Components/LoadingOverlay/LoadingOverlay'
import { DASHBAORD_DATA_ENDPOINT } from '@/Data/const'
import { ChartConfig } from '@/Data/Dashboard/DashboardDataClass'
import { pisaClient } from '@/Data/pisaClient'
import { isDevEnv } from '@/utils/isRuntimeEnv'
import { isString } from '@/utils/isString'

/**
 * Dashboard chart component properties
 */
export interface DashboardChartProps extends ChartConfig {
  id: string
}

interface FilterOption {
  _id: string
  name: string
}

interface ChartFilter {
  _id: string
  name: string
  options: FilterOption[]
  currentId: string
}

/**
 * Converts modern filters to legacy format for ChartHeader compatibility
 */
function convertFiltersToLegacyFormat(
  filters: ChartFilter[],
): LegacyChartFilter[] {
  return filters.map((filter) => ({
    ID: filter._id,
    NAM: filter.name,
    OPT: filter.options.map((opt) => ({
      ID: opt._id,
      NAM: opt.name,
    })),
    CUR_ID: filter.currentId,
  }))
}

export const DashboardChartComponent: React.FC<DashboardChartProps> = ({
  id,
  element,
  title,
}: DashboardChartProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<ChartData | null>(null)
  const [filterOptions, setFilterOptions] = useState<LegacyChartFilter[]>([])
  const [selectedFilterId, setSelectedFilterId] = useState<string>('')

  const filterChange = (filters: CurrentChartFilter[]) => {
    if (filters && filters.length > 0 && filters[0]) {
      setSelectedFilterId(filters[0].CUR_ID)
    }
  }

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      if (isDevEnv()) {
        console.log('>>> Fetching V2 chart data', id)
      }
      if (!isString(id)) {
        setIsLoading(false)
        return
      }
      pisaClient(DASHBAORD_DATA_ENDPOINT)
        .then((client) => {
          return client.get(id, {
            params: {
              filter: selectedFilterId || undefined,
              format: 'v2',
            },
          })
        })
        .then((response) => {
          if (isDevEnv()) {
            console.log(
              `>>> V2 Dashboard data id="${id}" element="${element}" title="${title}":`,
              response,
            )
          }
          const v2Data = response as ChartData
          setData(v2Data)

          // Extract filters from metadata if available and convert to V1 format
          const hasFilters =
            v2Data.metaData?.filters && v2Data.metaData.filters.length > 0
          if (hasFilters && v2Data.metaData.filters) {
            setFilterOptions(
              convertFiltersToLegacyFormat(v2Data.metaData.filters),
            )
          } else {
            setFilterOptions([])
          }
        })
        .catch((error) => {
          console.error(error, id)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    fetchData()
  }, [id, selectedFilterId, element, title])

  const metadata = data?.metaData
  const chartType = getChartType(data)
  const showLegend = metadata?.legendPosition !== 'none'

  return (
    <LoadingOverlay loading={isLoading} classes="dxp-dashboard-overlay">
      <div className="dxp-dashboard-item card">
        {metadata ? (
          <div
            id={id}
            className={`chartWrapper DXPChartWidget ${chartType}-chart DXPDashboardChartWrapper`}
          >
            <ChartHeader
              title={metadata.title}
              subTitle={metadata.subTitle}
              rawData={data?.rawData}
              filters={filterOptions}
              onFilterChange={filterChange}
            />
            {createChartElement(chartType, data, showLegend)}
          </div>
        ) : (
          <div className="skeleton" style={{ minHeight: '200px' }}></div>
        )}
      </div>
    </LoadingOverlay>
  )
}

/**
 * Returns the type of chart based on the V2 data provided
 * @param data - The V2 data object passed from the widget
 * @returns The type of chart
 */
function getChartType(
  data: ChartData | null,
): (typeof PSA_CHART_TYPES)[keyof typeof PSA_CHART_TYPES] {
  const elementType = data?.metaData?.elementType

  switch (elementType) {
    case 'barChart':
    case 'stackedBarChart':
      return PSA_CHART_TYPES.acBar
    case 'lineChart':
      return PSA_CHART_TYPES.acLines
    case 'pieChart':
      return PSA_CHART_TYPES.acPie
    case 'radialChart':
    case 'staticFunnelChart':
    case 'dynamicFunnelChart':
      return PSA_CHART_TYPES.acRadial
    case 'gaugeChart':
      return PSA_CHART_TYPES.gauge
    case 'list':
      return PSA_CHART_TYPES.watchList
    default:
      // Default to bar chart
      return PSA_CHART_TYPES.acBar
  }
}

/**
 * creates the chart element for V2 data
 * @param chartType the chart type
 * @param data chart data
 * @param showLegend flag whether to show the legend
 * @returns
 */
function createChartElement(
  chartType: string,
  data: ChartData | null,
  showLegend: boolean,
): React.ReactElement {
  // No need for try-catch here since each chart component handles its own errors
  switch (chartType) {
    case PSA_CHART_TYPES.acRadial:
      return (
        <AcRadialChart salesData={data || undefined} showLegend={showLegend} />
      )
    case PSA_CHART_TYPES.acPie:
      return (
        <AcPieChart salesData={data || undefined} showLegend={showLegend} />
      )
    case PSA_CHART_TYPES.acLines:
      return (
        <AcLinesChart salesData={data || undefined} showLegend={showLegend} />
      )
    case PSA_CHART_TYPES.gauge:
      return <GaugeChart salesData={data || undefined} />
    case PSA_CHART_TYPES.watchList:
      return <Watchlist salesData={data || undefined} />
    default:
    case PSA_CHART_TYPES.acBar:
      return (
        <AcBarChart salesData={data || undefined} showLegend={showLegend} />
      )
  }
}
